import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from '@mui/material';
import PaymentMethodCard from './PaymentMethodCard';
import {
  PaymentMethod,
  getPaymentMethods,
  createSetupSession,
  removePaymentMethod,
  setDefaultPaymentMethod,
} from './paymentApi';

const PaymentMethods: React.FC = () => {
  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [addingMethod, setAddingMethod] = useState(false);

  // @ts-ignore
  const orgId: string | undefined = (global as any)?.me?.orgID;

  const fetchMethods = useCallback(async () => {
    if (!orgId) {
      setError('Organization not found');
      setLoading(false);
      return;
    }
    try {
      setError(null);
      setLoading(true);
      const res = await getPaymentMethods(orgId);
      if (res.success && res.data) {
        setMethods(res.data.paymentMethods);
      } else {
        setError(res.error || 'Failed to load payment methods');
      }
    } catch (err) {
      setError('Unable to load payment methods. Check your connection.');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  // Fetch on mount and detect Stripe return
  useEffect(() => {
    fetchMethods();

    // Detect return from Stripe setup
    const params = new URLSearchParams(window.location.search);
    if (params.get('setup') === 'success') {
      // Clean up the URL
      const url = new URL(window.location.href);
      url.searchParams.delete('setup');
      window.history.replaceState({}, '', url.toString());
    }
  }, [fetchMethods]);

  const handleAddMethod = async () => {
    if (!orgId) return;
    try {
      setAddingMethod(true);
      const currentUrl = window.location.origin + window.location.pathname;
      const res = await createSetupSession(
        orgId,
        `${currentUrl}?setup=success`,
        `${currentUrl}?setup=cancelled`
      );
      if (res.success && res.data?.url) {
        window.location.href = res.data.url;
      } else {
        setError(res.error || 'Failed to start payment setup');
        setAddingMethod(false);
      }
    } catch {
      setError('Unable to start payment setup. Please try again.');
      setAddingMethod(false);
    }
  };

  const handleRemove = async (pmId: string) => {
    if (!orgId) return;
    try {
      setRemovingId(pmId);
      const res = await removePaymentMethod(orgId, pmId);
      if (res.success) {
        await fetchMethods();
      } else {
        setError(res.error || 'Failed to remove payment method');
      }
    } catch {
      setError('Unable to remove payment method. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleSetDefault = async (pmId: string) => {
    if (!orgId) return;
    try {
      setSettingDefaultId(pmId);
      const res = await setDefaultPaymentMethod(orgId, pmId);
      if (res.success) {
        await fetchMethods();
      } else {
        setError(res.error || 'Failed to set default payment method');
      }
    } catch {
      setError('Unable to set default payment method. Please try again.');
    } finally {
      setSettingDefaultId(null);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchMethods}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {methods.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No payment methods on file
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add a payment method so your team can purchase estimates.
          </Typography>
          <Button
            variant="contained"
            onClick={handleAddMethod}
            disabled={addingMethod}
          >
            {addingMethod ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Add Payment Method'
            )}
          </Button>
        </Box>
      ) : (
        <>
          {methods.map((m) => (
            <PaymentMethodCard
              key={m.id}
              method={m}
              onRemove={handleRemove}
              onSetDefault={handleSetDefault}
              isRemoving={removingId === m.id}
              isSettingDefault={settingDefaultId === m.id}
            />
          ))}
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleAddMethod}
              disabled={addingMethod}
            >
              {addingMethod ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Add Payment Method'
              )}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PaymentMethods;
